// src/hooks/useVersionCheck.js (Shell)
import { useEffect } from 'react';
import { hasNewVersion } from '../utils/versionTracker';

export const CHECK_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Production builds only: every 10 minutes, and whenever the tab is shown again, asks whether
 * a build this tab runs has been redeployed, and calls `onNewVersion` when one has.
 * @param {() => void} onNewVersion
 */
export function useVersionCheck(onNewVersion) {
    useEffect(() => {
        if (!import.meta.env.PROD) return undefined;

        let active = true;
        let checking = false;

        const check = async () => {
            if (checking) return;
            checking = true;
            try {
                if (await hasNewVersion() && active) onNewVersion();
            } finally {
                checking = false;
            }
        };

        const checkWhenShown = () => {
            if (document.visibilityState === 'visible') check();
        };

        const interval = setInterval(check, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', checkWhenShown);

        return () => {
            active = false;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', checkWhenShown);
        };
    }, [onNewVersion]);
}
